import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminFetch, AdminAuthError } from '../lib/adminApi';
import '../styles/admin-premios.css';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'https://giroexpress-ruleta-backend.onrender.com';

const MESSAGE_SEGMENTS = [
  'segmento_03', 'segmento_05', 'segmento_07', 'segmento_08',
  'segmento_10', 'segmento_12', 'segmento_13', 'segmento_14', 'segmento_15'
];

const DEFAULTS = {
  modal_icon: '',
  modal_title: '¡Sigue así!',
  modal_line_1: 'No te rindas',
  modal_line_2: 'Cada giro cuenta',
  modal_button_text: 'Girar de nuevo',
};

export default function AdminSegmentModals() {
  const navigate = useNavigate();
  const [configs, setConfigs] = useState({});
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({...DEFAULTS});
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [authError, setAuthError] = useState(null);

  const fetchConfigs = useCallback(async () => {
    try {
      const res = await adminFetch(`${API_URL}/api/admin/segment-modals`);
      if (res.status === 401 || res.status === 403) {
        setAuthError(res.status === 401
          ? 'Sesión inválida. Inicia sesión como administrador.'
          : 'No tienes permisos de administrador.');
        return;
      }
      const data = await res.json();
      if (data.success) {
        const map = {};
        (data.configs || []).forEach(c => { map[c.segment_id] = c; });
        setConfigs(map);
      }
    } catch (e) {
      if (e instanceof AdminAuthError) setAuthError(e.message);
      else console.error(e);
    }
    finally { setLoaded(true); }
  }, []);

  useEffect(() => { fetchConfigs(); }, [fetchConfigs]);

  const openEdit = (segId) => {
    const existing = configs[segId];
    setForm({
      modal_icon: existing?.modal_icon || '',
      modal_title: existing?.modal_title || '',
      modal_line_1: existing?.modal_line_1 || '',
      modal_line_2: existing?.modal_line_2 || '',
      modal_button_text: existing?.modal_button_text || '',
    });
    setEditing(segId);
  };

  const saveConfig = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const res = await adminFetch(`${API_URL}/api/admin/segment-modals/${editing}`, {
        method: 'POST',
        body: JSON.stringify(form)
      });
      if (res.status === 401 || res.status === 403) {
        alert(res.status === 401 ? 'Sesión inválida. Inicia sesión como administrador.' : 'No tienes permisos de administrador.');
        return;
      }
      const data = await res.json();
      if (data.success) {
        setEditing(null);
        fetchConfigs();
      }
    } catch (e) {
      if (e instanceof AdminAuthError) alert(e.message);
      else console.error(e);
    }
    finally { setSaving(false); }
  };

  const getPreview = (segId) => {
    const c = configs[segId];
    if (!c) return null;
    return c;
  };

  return (
    <div className="adm-container" data-testid="admin-segment-modals-page">
      <header className="adm-header">
        <button className="adm-back" onClick={() => navigate('/admin/premios')}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 18L9 12L15 6"/></svg>
        </button>
        <h1 className="adm-title">Modales de Segmentos</h1>
      </header>

      <p className="seg-desc">Configura el texto que ve el usuario cuando cae en un segmento tipo mensaje (sin premio).</p>

      {!loaded && <div className="adm-loading"><div className="adm-spinner" /></div>}

      {loaded && authError && (
        <div className="adm-error" data-testid="admin-auth-error" style={{ padding: '14px', background: 'rgba(255,40,40,0.08)', border: '1px solid rgba(255,40,40,0.4)', borderRadius: '8px', color: '#ff6b6b', textAlign: 'center', margin: '16px 0' }}>
          {authError}
        </div>
      )}

      {loaded && !authError && (
        <div className="seg-grid">
          {MESSAGE_SEGMENTS.map(segId => {
            const c = getPreview(segId);
            const num = segId.replace('segmento_', '');
            return (
              <div key={segId} className="seg-card" data-testid={`seg-card-${segId}`}>
                <div className="seg-card-head">
                  <span className="seg-num">S{num}</span>
                  <span className="seg-id">{segId}</span>
                  {c && <span className="seg-configured">Configurado</span>}
                </div>
                {c ? (
                  <div className="seg-preview">
                    {c.modal_icon && <span className="seg-prev-icon">{c.modal_icon}</span>}
                    <span className="seg-prev-title">{c.modal_title || DEFAULTS.modal_title}</span>
                    <span className="seg-prev-line">{c.modal_line_1 || DEFAULTS.modal_line_1}</span>
                  </div>
                ) : (
                  <div className="seg-preview seg-default">
                    <span className="seg-prev-title">{DEFAULTS.modal_title}</span>
                    <span className="seg-prev-line">{DEFAULTS.modal_line_1}</span>
                  </div>
                )}
                <button className="seg-edit-btn" onClick={() => openEdit(segId)} data-testid={`seg-edit-${segId}`}>
                  Editar
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Edit Modal */}
      {editing && (
        <div className="adm-modal-overlay" onClick={() => !saving && setEditing(null)}>
          <div className="adm-modal seg-modal" onClick={e => e.stopPropagation()} data-testid="seg-edit-modal">
            <h3 className="adm-modal-title">Editar modal — {editing}</h3>

            <div className="seg-form">
              <div className="adm-modal-field">
                <label>Icono (emoji)</label>
                <input
                  type="text"
                  placeholder="Ej: &#128170; &#127775; &#128293;"
                  value={form.modal_icon}
                  onChange={e => setForm(f => ({...f, modal_icon: e.target.value}))}
                  className="seg-input"
                  data-testid="seg-modal-icon"
                />
              </div>
              <div className="adm-modal-field">
                <label>Título *</label>
                <input
                  type="text"
                  placeholder={DEFAULTS.modal_title}
                  value={form.modal_title}
                  onChange={e => setForm(f => ({...f, modal_title: e.target.value}))}
                  className="seg-input"
                  data-testid="seg-modal-title"
                />
              </div>
              <div className="adm-modal-field">
                <label>Línea 1</label>
                <input
                  type="text"
                  placeholder={DEFAULTS.modal_line_1}
                  value={form.modal_line_1}
                  onChange={e => setForm(f => ({...f, modal_line_1: e.target.value}))}
                  className="seg-input"
                  data-testid="seg-modal-line1"
                />
              </div>
              <div className="adm-modal-field">
                <label>Línea 2</label>
                <input
                  type="text"
                  placeholder={DEFAULTS.modal_line_2}
                  value={form.modal_line_2}
                  onChange={e => setForm(f => ({...f, modal_line_2: e.target.value}))}
                  className="seg-input"
                  data-testid="seg-modal-line2"
                />
              </div>
              <div className="adm-modal-field">
                <label>Texto del botón</label>
                <input
                  type="text"
                  placeholder={DEFAULTS.modal_button_text}
                  value={form.modal_button_text}
                  onChange={e => setForm(f => ({...f, modal_button_text: e.target.value}))}
                  className="seg-input"
                  data-testid="seg-modal-button"
                />
              </div>
            </div>

            <div className="seg-live-preview">
              <span className="seg-lp-label">Vista previa</span>
              <div className="seg-lp-box">
                {(form.modal_icon) && <span className="seg-lp-icon">{form.modal_icon}</span>}
                <span className="seg-lp-title">{form.modal_title || DEFAULTS.modal_title}</span>
                <span className="seg-lp-line1">{form.modal_line_1 || DEFAULTS.modal_line_1}</span>
                <span className="seg-lp-line2">{form.modal_line_2 || DEFAULTS.modal_line_2}</span>
                <span className="seg-lp-btn">{form.modal_button_text || DEFAULTS.modal_button_text}</span>
              </div>
            </div>

            <div className="adm-modal-actions">
              <button className="adm-btn-cancel" onClick={() => setEditing(null)} disabled={saving}>Cancelar</button>
              <button className="adm-btn-submit" onClick={saveConfig} disabled={saving} data-testid="seg-save-btn">
                {saving ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
